import { useState, useEffect } from 'react';
import axios from '../api/axios';
import '../styles/InventoryCategories.css';

function InventoryCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/inventory/categories');
      setCategories(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      alert('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`/inventory/categories/${editingCategory.id}`, formData);
        alert('Category updated successfully');
      } else {
        await axios.post('/inventory/categories', formData);
        alert('Category created successfully');
      }
      handleCloseModal();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error.response?.data?.message || 'Failed to save category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await axios.delete(`/inventory/categories/${id}`);
      alert('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert(error.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '' });
  };

  return (
    <div className="ic-container">
      <div className="ic-header">
        <h1>Inventory Categories</h1>
        <button className="ic-btn-primary" onClick={() => setShowModal(true)}>
          + Add Category
        </button>
      </div>

      {loading ? (
        <div className="ic-loading">Loading categories...</div>
      ) : (
        <div className="ic-grid">
          {categories.length === 0 ? (
            <div className="ic-empty">
              <p>No categories found. Create your first category to get started.</p>
            </div>
          ) : (
            categories.map((category) => (
              <div key={category.id} className="ic-card">
                <div className="ic-card-header">
                  <h3>{category.name}</h3>
                  <div className="ic-card-actions">
                    <button className="ic-btn-icon" onClick={() => handleEdit(category)} title="Edit">
                      ✏️
                    </button>
                    <button className="ic-btn-icon" onClick={() => handleDelete(category.id)} title="Delete">
                      🗑️
                    </button>
                  </div>
                </div>
                {category.description && (
                  <p className="ic-card-description">{category.description}</p>
                )}
                <div className="ic-card-footer">
                  <span className="ic-badge">{category.itemCount || 0} items</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <div className="ic-modal-overlay" onClick={handleCloseModal}>
          <div className="ic-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ic-modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
              <button className="ic-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ic-form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Tools, Materials, Equipment"
                />
              </div>
              <div className="ic-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows="3"
                />
              </div>
              <div className="ic-modal-actions">
                <button type="button" className="ic-btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="ic-btn-primary">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryCategories;
